import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Phone, Mail, MapPin, Clock } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import StickyCallButton from '@/components/StickyCallButton';
import PageSEO from '@/components/PageSEO';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    service: '',
    message: '',
    emergency: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      toast.success('Vielen Dank! Ihre Anfrage wurde erfolgreich übermittelt.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        city: '',
        service: '',
        message: '',
        emergency: false,
      });
    } catch (error) {
      console.error('Fehler beim Senden:', error);
      toast.error('Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut oder rufen Sie uns direkt an.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags */}
      <PageSEO
        title="Kontakt | 24/7 Handwerkernotdienst NRW ☎ 0231 39083687"
        description="Kontaktieren Sie unseren 24/7 Handwerkernotdienst: ☎ 0231 39083687 oder per Kontaktformular. Schnelle Hilfe bei Notfällen im Ruhrgebiet."
        canonicalUrl="https://handwerkernotdienst.nrw/kontakt"
        breadcrumbs={[
          { name: 'Start', url: 'https://handwerkernotdienst.nrw/' },
          { name: 'Kontakt', url: 'https://handwerkernotdienst.nrw/kontakt' }
        ]}
      />
      
      <Header />
      <StickyCallButton />

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#e8f4f8] to-white py-16" data-testid="contact-hero">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4" data-testid="contact-title">
            Kontaktieren Sie uns
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Wir sind 24/7 für Sie erreichbar. Bei Notfällen rufen Sie uns bitte direkt an.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16" data-testid="contact-content">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Kontaktinformationen</h2>
              
              <div className="space-y-6 mb-8">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#0066cc] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">24/7 Notruf-Hotline</h3>
                    <a href="tel:+4923139083687" className="text-[#0066cc] text-lg font-bold hover:underline" data-testid="contact-phone">
                      +49 231 39083687
                    </a>
                    <p className="text-gray-600 text-sm mt-1">Rund um die Uhr erreichbar</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#0066cc] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">E-Mail</h3>
                    <a href="mailto:info@handwerkernotdienst.nrw" className="text-[#0066cc] hover:underline" data-testid="contact-email">
                      info@handwerkernotdienst.nrw
                    </a>
                    <p className="text-gray-600 text-sm mt-1">Für allgemeine Anfragen</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#0066cc] rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Adresse</h3>
                    <p className="text-gray-700">
                      Handwerkernotdienst NRW<br />
                      Schössow Dienstleistungen UG<br />
                      Neben dem Brand 10<br />
                      44135 Dortmund
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#0066cc] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Erreichbarkeit</h3>
                    <p className="text-gray-700">
                      24 Stunden am Tag<br />
                      7 Tage die Woche<br />
                      365 Tage im Jahr
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded">
                <h4 className="font-semibold text-red-900 mb-2">Bei Notfällen:</h4>
                <p className="text-red-800 mb-3">
                  Rufen Sie uns bitte direkt an. Das Kontaktformular ist für nicht-dringende Anfragen gedacht.
                </p>
                <a href="tel:+4923139083687" data-testid="emergency-call-button">
                  <Button className="bg-[#e63946] hover:bg-[#d62828] text-white font-bold">
                    <Phone className="w-4 h-4 mr-2" />
                    0231 39083687
                  </Button>
                </a>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Kontaktformular</h2>
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Ihr Name"
                    data-testid="form-name"
                  />
                </div>

                <div>
                  <Label htmlFor="email">E-Mail *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="ihre@email.de"
                    data-testid="form-email"
                  />
                </div>

                <div>
                  <Label htmlFor="phone">Telefon *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="0231 12345678"
                    data-testid="form-phone"
                  />
                </div>

                <div>
                  <Label htmlFor="city">Stadt / PLZ</Label>
                  <Input
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="z.B. Dortmund oder 44135"
                    data-testid="form-city"
                  />
                </div>

                <div>
                  <Label htmlFor="service">Gewünschte Leistung</Label>
                  <Input
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    placeholder="z.B. Schlüsseldienst, Elektro-Notdienst"
                    data-testid="form-service"
                  />
                </div>

                <div>
                  <Label htmlFor="message">Ihre Nachricht *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    placeholder="Beschreiben Sie Ihr Anliegen..."
                    data-testid="form-message"
                  />
                </div>

                <div className="flex items-start space-x-2">
                  <Checkbox
                    id="emergency"
                    checked={formData.emergency}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, emergency: checked }))}
                    data-testid="form-emergency"
                  />
                  <Label htmlFor="emergency" className="text-sm leading-tight cursor-pointer">
                    Dies ist ein Notfall und ich möchte schnellstmöglich kontaktiert werden.
                  </Label>
                </div>

                <div className="text-sm text-gray-600">
                  Mit dem Absenden bestätigen Sie, dass Sie unsere{' '}
                  <Link to="/datenschutz" className="text-[#0066cc] hover:underline">Datenschutzerklärung</Link>
                  {' '}gelesen haben.
                </div>

                <Button
                  type="submit"
                  size="lg"
                  className="w-full bg-[#0066cc] hover:bg-[#0052a3] text-white font-bold"
                  disabled={isSubmitting}
                  data-testid="form-submit"
                >
                  {isSubmitting ? 'Wird gesendet...' : 'Anfrage absenden'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;